import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Menu, X } from "lucide-react";
import { BRAND, KnownLockup } from "../Brand";
import { Cta, Eyebrow, H2, Lead, Section, Ticks } from "./parts";
import { TourButton } from "./Tour";
import { CareGroups, Hero, HowItWorks, LeaderView, PastorView, PeopleFirst, PlanningCenter, Privacy, Problem } from "./Sections";

const LINKS = [["How It Works", "#how"], ["Planning Center", "#planning-center"], ["Pricing", "#pricing"]];

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-[color:var(--paper-glass)] backdrop-blur-md border-b border-line" data-testid="mk-nav">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-6">
        <a href="#top" aria-label={BRAND.full}><KnownLockup size="sm" testid="mk-nav-brand" /></a>
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map(([l, h]) => <a key={h} href={h} className="text-[15px] text-ink-soft hover:text-ink transition-colors" data-testid={`mk-nav-${h.slice(1)}`}>{l}</a>)}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Link to="/sign-in" className="btn btn-quiet btn-sm" data-testid="mk-nav-login">Log In</Link>
          <Link to="/sign-up" className="btn btn-primary btn-sm" data-testid="mk-nav-get-started">Get Started</Link>
        </div>
        <button className="md:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mk-nav-menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
      </div>
      {open && (
        <div className="md:hidden border-t border-line bg-paper px-6 py-4 space-y-1" data-testid="mk-nav-mobile">
          {LINKS.map(([l, h]) => <a key={h} href={h} onClick={(e) => { e.preventDefault(); setOpen(false); setTimeout(() => document.querySelector(h)?.scrollIntoView({ behavior: "smooth" }), 50); }} className="block py-3 text-[17px] text-ink" data-testid={`mk-nav-mobile-${h.slice(1)}`}>{l}</a>)}
          <div className="flex gap-2 pt-3"><Link to="/sign-in" className="btn btn-outline flex-1" data-testid="mk-nav-mobile-login">Log In</Link><Link to="/sign-up" className="btn btn-primary flex-1" data-testid="mk-nav-mobile-get-started">Get Started</Link></div>
        </div>
      )}
    </header>
  );
};

const PLANS = [["Essential", 59, 5], ["Team", 79, 10, true], ["Church", 99, 25]];
const CORE = ["Unlimited people", "Today, Log Care and follow-ups", "Pastor Overview", "Care Groups and Care Rhythm", "Confidential notes and audit history", "Planning Center connection"];

const Pricing = () => (
  <Section id="pricing" tone="stone" testid="mk-pricing">
    <div className="max-w-[720px]">
      <Eyebrow>Pricing</Eyebrow>
      <H2 className="mt-5">Unlimited people on every plan.</H2>
      <Lead className="mt-5">Your price shouldn't increase because more people need to be known. Plans are based on the size of your care team, not your congregation.</Lead>
    </div>
    <div className="grid md:grid-cols-3 gap-5 mt-12">
      {PLANS.map(([name, price, users, popular]) => (
        <div key={name} className={`rounded-[22px] p-8 flex flex-col ${popular ? "bg-[color:var(--deep)] text-[color:var(--cream)] md:-translate-y-3" : "bg-paper border border-line"}`} data-testid={`mk-plan-${name.toLowerCase()}`}>
          <div className="flex items-center justify-between">
            <p className={`font-display font-semibold text-[22px] ${popular ? "" : "text-ink"}`}>{name}</p>
            {popular && <span className="text-[12px] font-semibold tracking-wide uppercase px-3 py-1 rounded-full bg-[color:var(--clay)] text-white" data-testid="mk-plan-popular">Most Popular</span>}
          </div>
          <p className="mt-6"><span className={`font-display font-semibold text-[48px] tracking-[-0.03em] ${popular ? "" : "text-ink"}`}>${price}</span><span className={popular ? "text-[color:var(--stone)]" : "text-ink-soft"}>/month</span></p>
          <p className={`mt-1 text-[16px] ${popular ? "text-[color:var(--stone)]" : "text-ink-soft"}`}>Up to {users} care-team users</p>
          <div className={`my-7 h-px ${popular ? "bg-[rgba(248,245,238,0.18)]" : "bg-line"}`} />
          <Ticks light={popular} items={CORE} />
          <div className="mt-8 pt-2 mt-auto"><Cta variant={popular ? "light" : "primary"} testid={`mk-plan-${name.toLowerCase()}-cta`} /></div>
        </div>
      ))}
    </div>
    <div className="mt-8 flex flex-col items-center gap-3 text-center">
      <p className="text-ink-soft text-[16px]" data-testid="mk-pricing-terms">Every plan includes everything in KNOWN. Month-to-month. Cancel anytime.</p>
      <TourButton label="Try it first" className="" testid="mk-pricing-tour" />
    </div>
  </Section>
);

const FounderQuote = () => (
  <section className="bg-paper border-y border-line" data-testid="mk-founder-quote">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7">
      <img src="/marketing/founder.jpeg" alt="Demonte Daniels" className="w-14 h-14 rounded-full object-cover object-top border border-line shrink-0" />
      <div className="flex-1">
        <p className="font-display text-[22px] sm:text-[26px] leading-snug text-ink" data-testid="mk-founder-quote-text">“The issue was never whether people cared.”</p>
        <p className="text-ink-soft text-[14px] mt-1">Demonte Daniels, Founder, KNOWN Church Care</p>
      </div>
      <a href="#founder" onClick={(e) => { e.preventDefault(); document.querySelector("#founder")?.scrollIntoView({ behavior: "smooth" }); }} className="inline-flex items-center gap-2 py-2.5 text-[15px] font-semibold text-[color:var(--deep)] hover:gap-3 transition-[gap]" data-testid="mk-founder-quote-link">Read the story <ArrowDown size={16} /></a>
    </div>
  </section>
);

const Founder = () => (
  <Section id="founder" testid="mk-founder" className="border-t border-line scroll-mt-[72px]">
    <div className="grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 lg:gap-16 items-center">
      <div className="hidden md:block w-full">
        <img src="/marketing/founder.jpeg" alt="Demonte Daniels, Founder of KNOWN Church Care" className="w-full aspect-[4/5] object-cover object-top rounded-[22px] border border-line" loading="lazy" data-testid="mk-founder-photo" />
      </div>
      <div>
        <Eyebrow>Why KNOWN exists</Eyebrow>
        <H2 className="mt-5">Built by a pastor who knows the problem firsthand.</H2>
        <div className="md:hidden mt-6 flex items-center gap-4" data-testid="mk-founder-id-mobile">
          <img src="/marketing/founder.jpeg" alt="Demonte Daniels" className="w-20 h-24 rounded-[16px] object-cover object-top border border-line shrink-0" loading="lazy" data-testid="mk-founder-photo-mobile" />
          <div className="pl-4 border-l-2 border-[color:var(--clay)]">
            <p className="font-display font-semibold text-[20px] text-ink">Demonte Daniels</p>
            <p className="text-ink-soft text-[14px] mt-0.5">Founder, KNOWN Church Care</p>
            <p className="text-ink-soft text-[14px]">Lead Pastor, Presence Church</p>
          </div>
        </div>
        <div className="mt-6 space-y-5 max-w-[620px]" data-testid="mk-founder-story">
          <Lead>KNOWN Church Care was created by Pastor Demonte Daniels after seeing how easily pastoral care can become scattered across texts, conversations, spreadsheets, personal notes, and memory.</Lead>
          <Lead className="hidden md:block">Leading a local church made the challenge clear: the issue was never whether people cared. The issue was giving caring people a simple system to know who needs attention, who owns the next step, and whether follow-through actually happened.</Lead>
          <Lead>KNOWN was built to help churches care for people with greater clarity without turning people into tasks or replacing the systems they already use.</Lead>
        </div>
        <div className="hidden md:block mt-8 pl-5 border-l-2 border-[color:var(--clay)]" data-testid="mk-founder-id">
          <p className="font-display font-semibold text-[22px] text-ink">Demonte Daniels</p>
          <p className="text-ink-soft text-[15px] mt-1">Founder, KNOWN Church Care</p>
          <p className="text-ink-soft text-[15px]">Lead Pastor, Presence Church</p>
        </div>
      </div>
    </div>
  </Section>
);

const FinalCta = () => (
  <Section tone="deep" testid="mk-final">
    <div className="max-w-[820px]">
      <H2 light>Your church already has people who care. KNOWN helps them care together.</H2>
      <Lead light className="mt-6 max-w-[620px]">Give your team one clear place to see needs, share responsibility, and make sure the next step actually happens.</Lead>
      <div className="flex flex-wrap gap-3 mt-9">
        <Cta variant="light" testid="mk-final-get-started" />
        <Link to="/sign-in" className="btn btn-lg border border-[rgba(248,245,238,0.35)] text-[color:var(--cream)] hover:bg-[rgba(248,245,238,0.08)]" data-testid="mk-final-login">Log In</Link>
      </div>
    </div>
  </Section>
);

const Footer = () => (
  <footer className="bg-paper border-t border-line" data-testid="mk-footer">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row gap-6 sm:items-center justify-between">
      <div><KnownLockup size="sm" /><p className="text-ink-faint text-[14px] mt-3">{BRAND.tagline}</p></div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[15px] text-ink-soft">
        {LINKS.map(([l, h]) => <a key={h} href={h} className="py-2.5 hover:text-ink">{l}</a>)}
        <Link to="/sign-in" className="py-2.5 hover:text-ink">Log In</Link>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  useEffect(() => {
    document.body.className = "pal-moss mk";
    document.title = "KNOWN Church Care · Keep people known. Keep care moving.";
    return () => { document.body.className = "pal-moss"; };
  }, []);
  return (
    <div id="top" className="mk bg-paper text-ink scroll-smooth" data-testid="marketing-home">
      <Nav />
      <main>
        <Hero /><FounderQuote /><Problem /><HowItWorks /><PastorView /><LeaderView /><PlanningCenter /><PeopleFirst /><CareGroups /><Privacy /><Founder /><Pricing /><FinalCta />
      </main>
      <Footer />
    </div>
  );
}
